import { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./api";

function num(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export default function PrinterSettings({ site }) {
  const [labelSizes, setLabelSizes] = useState([]);
  const [printers] = useState(["Default"]); // Phase 1: logical printers
  const [printerName, setPrinterName] = useState("Default");
  const [labelName, setLabelName] = useState("");

  const [status, setStatus] = useState("Select a label size to begin.");
  const [err, setErr] = useState("");

  // layout state (mirrors desktop)
  const [dpi, setDpi] = useState(300);
  const [qrSize, setQrSize] = useState(220);
  const [textSize, setTextSize] = useState(16);

  const [showMirrored, setShowMirrored] = useState(false);
  const [showMirroredQR, setShowMirroredQR] = useState(true);
  const [showMirroredText, setShowMirroredText] = useState(true);

  const [qr, setQr] = useState({ x: 20, y: 20 });
  const [txt, setTxt] = useState({ x: 20, y: 260 });
  const [qrM, setQrM] = useState({ x: 20, y: 20 });
  const [txtM, setTxtM] = useState({ x: 20, y: 260 });

  const canvasRef = useRef(null);
  const dragRef = useRef({ active: false, target: null, lastX: 0, lastY: 0 });
  const hitRef = useRef(null);
  const selectedRef = useRef(null);

  const labelMap = useMemo(() => {
    const m = new Map();
    (labelSizes || []).forEach((r) => {
      const nm = String(r.label_size || "").trim();
      const w = Number(r.width_in);
      const h = Number(r.height_in);
      if (nm && Number.isFinite(w) && Number.isFinite(h)) m.set(nm, { w, h });
    });
    return m;
  }, [labelSizes]);

  // load label sizes
  useEffect(() => {
    let alive = true;
    (async () => {
      setErr("");
      try {
        const r = await apiFetch("/api/printer/label-sizes");
        if (!r.ok) throw new Error(await r.text());
        const data = await r.json();
        if (!alive) return;
        setLabelSizes(data || []);
      } catch (e) {
        if (!alive) return;
        setErr(String(e.message || e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // load settings when site+printer+label changes
  useEffect(() => {
    if (!site || !printerName || !labelName) return;
    let alive = true;

    (async () => {
      setStatus("Loading label profile…");
      try {
        const qs = new URLSearchParams({
          site,
          printer_name: printerName,
          label_size: labelName,
        });
        const r = await apiFetch(`/api/printer/settings/get?${qs}`);
        if (!r.ok) throw new Error(await r.text());
        const out = await r.json();
        if (!alive) return;

        if (!out?.found) {
          // defaults (same as desktop: no carry-over)
          setDpi(300);
          setQrSize(220);
          setTextSize(16);
          setShowMirrored(false);
          setShowMirroredQR(true);
          setShowMirroredText(true);
          setQr({ x: 20, y: 20 });
          setTxt({ x: 20, y: 260 });
          setQrM({ x: 20, y: 20 });
          setTxtM({ x: 20, y: 260 });
          selectedRef.current = null;
          setStatus("No profile for this printer/label. Using defaults (adjust then Save).");
          return;
        }

        const row = out.row || {};
        setDpi(num(row.dpi, 300));
        setQrSize(num(row.qr_size_px, 220));
        setTextSize(num(row.text_size, 16));
        setShowMirrored(!!row.show_mirrored);
        setShowMirroredQR(row.show_mirrored_qr !== false);
        setShowMirroredText(row.show_mirrored_text !== false);

        setQr({ x: num(row.qr_x, 20), y: num(row.qr_y, 20) });
        setTxt({ x: num(row.text_x, 20), y: num(row.text_y, 260) });
        setQrM({ x: num(row.qr_m_x, 20), y: num(row.qr_m_y, 20) });
        setTxtM({ x: num(row.text_m_x, 20), y: num(row.text_m_y, 260) });

        selectedRef.current = null;
        setStatus("Loaded label profile.");
      } catch (e) {
        if (!alive) return;
        setStatus("Failed to load label profile.");
        setErr(String(e.message || e));
      }
    })();

    return () => {
      alive = false;
    };
  }, [site, printerName, labelName]);

  // draw preview
  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    labelName,
    dpi,
    qrSize,
    textSize,
    showMirrored,
    showMirroredQR,
    showMirroredText,
    qr,
    txt,
    qrM,
    txtM,
    labelSizes,
  ]);

  function demoText() {
    return (
      `Site: ${site || "N/A"}\n` +
      "Part Number: PN-123456\n" +
      "Description: Sample Part\n" +
      "Location: A1\n" +
      "Shelve: S1\n" +
      "Row: R1\n" +
      `Date: ${new Date().toISOString().slice(0, 10)}`
    );
  }

  function measureMultiline(ctx, text, lineHeight) {
    const lines = String(text || "").split("\n");
    let maxW = 0;
    lines.forEach((ln) => {
      const w = ctx.measureText(ln).width;
      if (w > maxW) maxW = w;
    });
    return { w: Math.ceil(maxW), h: Math.ceil(lines.length * lineHeight) };
  }

  function drawMultiline(ctx, text, x, y, lineHeight) {
    const lines = String(text || "").split("\n");
    let yy = y;
    lines.forEach((ln) => {
      ctx.fillText(ln, x, yy);
      yy += lineHeight;
    });
  }

  function draw() {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;

    const dims = labelMap.get(labelName);
    ctx.clearRect(0, 0, cv.width, cv.height);

    hitRef.current = null;

    if (!labelName || !dims) {
      ctx.fillStyle = "#6b7280";
      ctx.font = "14px Arial";
      ctx.fillText("Select a label size to begin.", 20, 30);
      return;
    }

    const pad = 30;
    const wIn = dims.w;
    const hIn = dims.h;

    const scale = Math.min((cv.width - pad * 2) / wIn, (cv.height - pad * 2) / hIn);
    const x0 = (cv.width - wIn * scale) / 2;
    const y0 = (cv.height - hIn * scale) / 2;

    const pxToCanvas = (p) => (p / dpi) * scale;

    // label border
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.strokeRect(x0, y0, wIn * scale, hIn * scale);

    const text = demoText();

    // QR box
    const qrW = pxToCanvas(qrSize);
    const qrX = x0 + pxToCanvas(qr.x);
    const qrY = y0 + pxToCanvas(qr.y);

    // Text origin
    const tx = x0 + pxToCanvas(txt.x);
    const ty = y0 + pxToCanvas(txt.y);

    // draw QR
    ctx.fillStyle = "#f3f4f6";
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 1;
    ctx.fillRect(qrX, qrY, qrW, qrW);
    ctx.strokeRect(qrX, qrY, qrW, qrW);
    ctx.fillStyle = "#111827";
    ctx.font = "bold 14px Arial";
    ctx.fillText("QR", qrX + 10, qrY + 22);

    // draw Text + bbox
    ctx.fillStyle = "#111827";
    const fontPx = Math.max(12, Math.round(textSize));
    ctx.font = `bold ${fontPx}px Arial`;
    const lineH = fontPx + 6;

    const textBox = measureMultiline(ctx, text, lineH);
    drawMultiline(ctx, text, tx, ty, lineH);

    // mirrored
    let qrMX = null,
      qrMY = null,
      txMX = null,
      tyMY = null;
    let mTextBox = null;

    if (showMirrored) {
      if (showMirroredQR) {
        qrMX = x0 + pxToCanvas(qrM.x);
        qrMY = y0 + pxToCanvas(qrM.y);
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#6b7280";
        ctx.strokeRect(qrMX, qrMY, qrW, qrW);
        ctx.setLineDash([]);
        ctx.fillStyle = "#6b7280";
        ctx.font = "bold 12px Arial";
        ctx.fillText("QR M", qrMX + 8, qrMY + 20);
      }

      if (showMirroredText) {
        txMX = x0 + pxToCanvas(txtM.x);
        tyMY = y0 + pxToCanvas(txtM.y);
        ctx.fillStyle = "#6b7280";
        ctx.font = `bold ${fontPx}px Arial`;
        mTextBox = measureMultiline(ctx, text, lineH);
        drawMultiline(ctx, text, txMX, tyMY, lineH);
      }
    }

    // hit boxes (canvas coords)
    hitRef.current = {
      label: { x0, y0, x1: x0 + wIn * scale, y1: y0 + hIn * scale, scale, wIn, hIn },

      qr: { x0: qrX, y0: qrY, x1: qrX + qrW, y1: qrY + qrW },
      txt: { x0: tx, y0: ty, x1: tx + textBox.w, y1: ty + textBox.h },

      qr_m: qrMX != null && qrMY != null ? { x0: qrMX, y0: qrMY, x1: qrMX + qrW, y1: qrMY + qrW } : null,
      txt_m:
        txMX != null && tyMY != null && mTextBox
          ? { x0: txMX, y0: tyMY, x1: txMX + mTextBox.w, y1: tyMY + mTextBox.h }
          : null,
    };

    // selection highlight
    const sel = selectedRef.current;
    if (sel && hitRef.current && hitRef.current[sel]) {
      const b = hitRef.current[sel];
      ctx.save();
      ctx.strokeStyle = "#2563eb";
      ctx.lineWidth = 3;
      ctx.strokeRect(b.x0 - 3, b.y0 - 3, b.x1 - b.x0 + 6, b.y1 - b.y0 + 6);
      ctx.restore();
    }
  }

  function pickTarget(mx, my) {
    const hit = hitRef.current;
    if (!hit) return null;

    const SLOP = 28; // bigger for phone fingers

    // Prefer QR first (smaller), then text.
    const order = ["qr", "qr_m", "txt", "txt_m"];

    for (const k of order) {
      const b = hit[k];
      if (!b) continue;

      const x0 = b.x0 - SLOP;
      const y0 = b.y0 - SLOP;
      const x1 = b.x1 + SLOP;
      const y1 = b.y1 + SLOP;

      if (mx >= x0 && mx <= x1 && my >= y0 && my <= y1) return k;
    }
    return null;
  }

  function clientToCanvas(clientX, clientY) {
    const cv = canvasRef.current;
    if (!cv) return null;
  
    const rect = cv.getBoundingClientRect();
  
    // Convert CSS pixel coords -> canvas pixel coords
    const scaleX = cv.width / rect.width;
    const scaleY = cv.height / rect.height;
  
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
  
    return { x, y };
  }

  
  // Shared down/move/up (used by Pointer + Touch)
  function downAt(clientX, clientY, pointerId = 0) {
    const cv = canvasRef.current;
    if (!cv) return;
  
    const pt = clientToCanvas(clientX, clientY);
    if (!pt) return;
  
    const mx = pt.x;
    const my = pt.y;
  
    const tgt = pickTarget(mx, my);
    selectedRef.current = tgt;
  
    dragRef.current.active = !!tgt;
    dragRef.current.target = tgt;
    dragRef.current.lastX = mx;
    dragRef.current.lastY = my;
  
    draw(); // show selection box immediately
  }

  function moveAt(clientX, clientY) {
    if (!dragRef.current.active) return;
  
    const hit = hitRef.current;
    if (!hit || !hit.label) return;
  
    const pt = clientToCanvas(clientX, clientY);
    if (!pt) return;
  
    const mx = pt.x;
    const my = pt.y;
  
    const dx = mx - dragRef.current.lastX;
    const dy = my - dragRef.current.lastY;
    dragRef.current.lastX = mx;
    dragRef.current.lastY = my;
  
    const scale = hit.label.scale;
  
    // canvas px -> printer px
    const dxPrinter = Math.round((dx / scale) * dpi);
    const dyPrinter = Math.round((dy / scale) * dpi);
  
    const dims = labelMap.get(labelName);
    if (!dims) return;
  
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
    const lw = Math.round(dims.w * dpi);
    const lh = Math.round(dims.h * dpi);
  
    const tgt = dragRef.current.target;
  
    const move = (setter) => {
      setter((p) => ({
        x: clamp(p.x + dxPrinter, 0, lw),
        y: clamp(p.y + dyPrinter, 0, lh),
      }));
    };
  
    if (tgt === "qr") move(setQr);
    else if (tgt === "txt") move(setTxt);
    else if (tgt === "qr_m") move(setQrM);
    else if (tgt === "txt_m") move(setTxtM);
  }

  function up(pointerId = 0) {
    dragRef.current.active = false;
    dragRef.current.target = null;
    const cv = canvasRef.current;
    cv?.releasePointerCapture?.(pointerId);
  }

  // Pointer events (desktop + modern mobile)
  function pointerDown(e) {
    e.preventDefault();
    e.stopPropagation();
    const cv = canvasRef.current;
    if (cv?.setPointerCapture) cv.setPointerCapture(e.pointerId);
    downAt(e.clientX, e.clientY, e.pointerId);
  }

  function pointerMove(e) {
    if (!dragRef.current.active) return;
    e.preventDefault();
    e.stopPropagation();
    moveAt(e.clientX, e.clientY);
  }

  function pointerUp(e) {
    e.preventDefault?.();
    e.stopPropagation?.();
    up(e.pointerId);
  }

  // Touch fallback (older iOS / WebViews)
  function touchStart(e) {
    e.preventDefault();
    e.stopPropagation();
    const t = e.touches && e.touches[0];
    if (!t) return;
    downAt(t.clientX, t.clientY, 0);
  }

  function touchMove(e) {
    if (!dragRef.current.active) return;
    e.preventDefault();
    e.stopPropagation();
    const t = e.touches && e.touches[0];
    if (!t) return;
    moveAt(t.clientX, t.clientY);
  }

  function touchEnd(e) {
    e.preventDefault?.();
    e.stopPropagation?.();
    up(0);
  }

  async function saveSettings() {
    setErr("");
    if (!site) return setErr("Site missing.");
    if (!printerName) return setErr("Select a printer name.");
    if (!labelName) return setErr("Select a label size.");

    const payload = {
      site,
      printer_name: printerName,
      label_size: labelName,
      dpi,
      qr_size_px: qrSize,
      text_size: textSize,
      qr_x: qr.x,
      qr_y: qr.y,
      text_x: txt.x,
      text_y: txt.y,
      show_mirrored: showMirrored,
      show_mirrored_qr: showMirroredQR,
      show_mirrored_text: showMirroredText,
      qr_m_x: qrM.x,
      qr_m_y: qrM.y,
      text_m_x: txtM.x,
      text_m_y: txtM.y,
    };

    try {
      const r = await apiFetch("/api/printer/settings/save", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      setStatus("Saved label profile.");
    } catch (e) {
      setErr(String(e.message || e));
    }
  }

  function testPrint() {
    const cv = canvasRef.current;
    if (!cv) return;
    const dataUrl = cv.toDataURL("image/png");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<html><head><title>Label Test</title></head><body style="margin:0;padding:20px;font-family:Arial;">`
    );
    w.document.write(`<h3 style="margin:0 0 12px 0;">Test Label</h3>`);
    w.document.write(
      `<img src="${dataUrl}" style="max-width:100%;border:1px solid #ddd;border-radius:8px;" />`
    );
    w.document.write(`<script>window.onload=()=>{window.print();}</script>`);
    w.document.write(`</body></html>`);
    w.document.close();
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>Printer Profile</div>
          <select
            value={printerName}
            onChange={(e) => setPrinterName(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid #e5e7eb" }}
          >
            {printers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            Phase 1: use OS printer selection in print dialog.
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>Label Size</div>
          <select
            value={labelName}
            onChange={(e) => setLabelName(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid #e5e7eb" }}
          >
            <option value="">Select…</option>
            {Array.from(labelMap.keys()).map((nm) => (
              <option key={nm} value={nm}>
                {nm}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>QR Size (px)</div>
          <input
            type="number"
            value={qrSize}
            onChange={(e) => setQrSize(num(e.target.value, 220))}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid #e5e7eb" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>Text Size</div>
          <input
            type="number"
            value={textSize}
            onChange={(e) => setTextSize(num(e.target.value, 16))}
            style={{ width: "100%", padding: "10px 12px", borderRadius: 12, border: "1px solid #e5e7eb" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "end", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#374151" }}>
            <input type="checkbox" checked={showMirrored} onChange={(e) => setShowMirrored(e.target.checked)} />
            Mirrored
          </label>
          {showMirrored && (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#374151" }}>
                <input type="checkbox" checked={showMirroredQR} onChange={(e) => setShowMirroredQR(e.target.checked)} />
                QR
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#374151" }}>
                <input type="checkbox" checked={showMirroredText} onChange={(e) => setShowMirroredText(e.target.checked)} />
                Text
              </label>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <button
          onClick={saveSettings}
          type="button"
          style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid #1d4ed8", background: "#2563eb", color: "white", fontWeight: 800 }}
        >
          Save Setting
        </button>
        <button
          onClick={testPrint}
          type="button"
          style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid #16a34a", background: "#22c55e", color: "white", fontWeight: 800 }}
        >
          Test Print
        </button>
        <div style={{ paddingTop: 10, fontSize: 13, color: "#6b7280", fontWeight: 700 }}>{status}</div>
      </div>

      {err && <div style={{ color: "#b91c1c", fontWeight: 800, marginBottom: 10 }}>{err}</div>}

      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
        Preview (tap QR/Text to select, then drag)
      </div>

      <canvas
        ref={canvasRef}
        width={900}
        height={520}
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerUp}
        onPointerCancel={pointerUp}
        onTouchStart={touchStart}
        onTouchMove={touchMove}
        onTouchEnd={touchEnd}
        onTouchCancel={touchEnd}
        style={{
          width: "100%",
          border: "1px solid #e5e7eb",
          borderRadius: 16,
          background: "white",
          touchAction: "none",
        }}
      />
    </div>
  );
}
